import React, { useCallback, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import HTMLRenderer from 'react-html-renderer'

import { fillArray } from 'lib/arrayUtils'
import { getCruiseCabinLocationCheckoutPath } from 'lib/paths'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pushWithRegion } from 'actions/NavigationActions'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import Pill from 'components/Luxkit/Pill'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import FormatCurrency from 'components/Common/FormatCurrency'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import { CruiseCheckoutItems } from 'components/Cruises/CruiseCheckoutHelper'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import AutoTooltip from 'components/Common/AutoTooltip'
import AutoTooltipContent from 'components/Common/AutoTooltip/AutoTooltipContent'
import { generateCruiseCheckoutItem } from 'checkout/lib/utils/cruises/cart'
import Label from 'components/Luxkit/Label/Label'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import { addToCart, cruiseSelectCabinRate } from 'analytics/eventDefinitions'
import { fireEcommerceGTMEvent, fireInteractionEvent } from 'api/googleTagManager'
import Modal from 'components/Luxkit/Modal/Modal'

const RateDetails = styled.div`
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  margin: ${rem(24)} 0 0 0;

  display: flex;
  gap: ${rem(12)};
  flex-direction: column;

  ${mediaQueryUp.sm} {
    display: grid;
    grid-column-gap: ${rem(24)};
    grid-template-columns: 1fr 1.2fr 1fr;
  }
`

const SkeletonInclusions = styled(LoadingBox)`
  width: 80%;
  border-radius: ${props => props.theme.borderRadius.large};
  margin: ${rem(8)} 0;
  min-height: ${rem(12)};
`

const InclusionsContent = styled.div`
  > * + * {
    margin: ${rem(8)} 0;
  }
`

const InclusionList = styled.ul`
  margin: 0 0 0 ${rem(12)};

  ${mediaQueryUp.sm} {
    margin: 0;
  }
`

const GridColumn = styled.div`
  width: 100%;

  &.sm-up-flex-column {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;

    ${mediaQueryUp.sm} {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`

const SelectRateButton = styled(TextButton)`
  width: ${rem(100)};
  height: ${rem(40)};

  ${mediaQueryUp.sm} {
    width: ${rem(160)};
    margin: ${rem(8)} 0;
  }
`

const PromotionExclusive = styled(Caption)`
  margin-bottom: ${rem(4)};
  color: ${({ theme }) => theme.palette.product.exclusive};
`

const StyledAutoTooltip = styled(AutoTooltip)`
  > span {
    display: flex;
    align-items: center;
  }
`

function OnboardTooltipInfo() {
  return (
    <StyledAutoTooltip>
      <SolidInfoCircleIcon colour="neutral-two" size={14} />
      <AutoTooltipContent width={100} position="below" type="dark" alignment="left">
        <BodyText variant="small">Use on specialty dining, premium drinks, shore excursions or spa treatments onboard.</BodyText>
      </AutoTooltipContent>
    </StyledAutoTooltip>
  )
}

function GuaranteedCabinTooltip() {
  const text = `A ‘Guarantee’ booking means you may book a stateroom of a guaranteed category type (Inside, Oceanview, Balcony, or Suite)
on your chosen ship and cruise. However, the exact grade, number and location of the stateroom on the ship will be allocated by the cruise
line at their discretion and at any time up until checking in at the Port.`

  return (
    <StyledAutoTooltip>
      <LineInfoCircleIcon colour="neutral-two" size={14} />
      <AutoTooltipContent width={250} position="above" type="dark" alignment="left">
        <BodyText variant="small">{text}</BodyText>
      </AutoTooltipContent>
    </StyledAutoTooltip>
  )
}

interface Props extends CruiseCheckoutItems {
  rateTitle: string;
  bookingId: string;
  ratePrice: number;
  cabinCode: string;
  ratePromotionTitle?: string;
  cabinRateDetailsError: boolean | null
  cabinRateDetailsLoading: boolean | null
  onboardCredits?: App.CruiseOnboardCredits;
  cabinRateDetails: App.CruiseCabinRateDetailsResponse | null
  isNccfIncluded?: boolean
  isTaxesIncluded?: boolean
  tax?: number;
  cabinStatus?: string;
  ratePrices?: App.CruiseRatePrices;
  rate: App.CruiseCabinRateView;
}

function CabinRateDetails(props: Props) {
  const {
    offer,
    cabinCode,
    bookingId,
    rateTitle,
    sessionId,
    ratePrice,
    cabinCategory,
    onboardCredits,
    cabinRateDetails,
    cabinRateDetailsError,
    cabinRateDetailsLoading,
    occupancy,
    ratePromotionTitle,
    // isNccfIncluded, // @TODO
    // isTaxesIncluded,
    // tax,
    cabinStatus,
    ratePrices,
    rate,
  } = props

  const dispatch = useAppDispatch()
  const [viewAllInclusions, onToggleViewAllInclusions] = useToggle()
  const [showTsAndCs, onToggleTsAndCs] = useToggle()

  const departureId = useAppSelector(
    (state) => state.cruise.departureId,
  )

  const dates = useMemo(() => {
    const departureItem = Object.values(offer.departures)
      .flat()
      .find((departure) => departure.id === departureId)

    return {
      startDate: departureItem.departureDate,
      endDate: departureItem.arrivalDate,
    }
  }, [offer.departures, departureId])

  const onSelectRate = useCallback(() => {
    const quantity = occupancy[0].adults + occupancy[0].children + occupancy[0].infants
    const price = ratePrice * (occupancy[0].adults + occupancy[0].children + occupancy[0].infants)
    const cartCurrency = offer?.lowestDeparturePrice?.currencyCode
    fireInteractionEvent(cruiseSelectCabinRate(rate.componentId))
    fireEcommerceGTMEvent(addToCart(offer, cartCurrency, price, quantity, 'cruise'))
    const item = generateCruiseCheckoutItem(
      offer.id,
      departureId,
      dates,
      occupancy[0],
      bookingId,
      sessionId,
      cabinCode,
      cabinCategory.category, // componentId
      ratePrices,
    )

    // Assumption: Only a single checkout item (cabin) per cart
    const items = [item]
    dispatch(addToCheckoutCart(items))

    const checkoutPath = getCruiseCabinLocationCheckoutPath()
    dispatch(pushWithRegion(checkoutPath))
  }, [
    dispatch,
    cabinCategory,
    sessionId,
    offer,
    departureId,
    occupancy,
    cabinCode,
    bookingId,
    dates,
    ratePrice,
    ratePrices,
    rate.componentId,
  ])

  const handleInclusionContent = useMemo(
    (): string => {
      if (cabinRateDetails) {
        return viewAllInclusions ?
          cabinRateDetails.rateDetails :
          cabinRateDetails.detailsPreview
      } else ''
    }, [cabinRateDetails, viewAllInclusions],
  )

  const hasDetailsContent = useMemo(() => {
    return !!((!cabinRateDetailsError && !cabinRateDetailsLoading) && cabinRateDetails)
  }, [cabinRateDetailsError, cabinRateDetailsLoading, cabinRateDetails])

  const isGuaranteedCabin = useMemo(() => (cabinStatus === 'Guarantee'), [cabinStatus])

  return (
    <RateDetails className="rate-details-container">
      <GridColumn>
        {ratePromotionTitle && (
          <HTMLRenderer
            html={ratePromotionTitle}
            components={{
              p: ({ children }) => <PromotionExclusive variant="medium" weight="bold">{children}</PromotionExclusive>,
              span: ({ children }) => <PromotionExclusive variant="medium" weight="bold">{children}</PromotionExclusive>,
            }}
          />
        )}
        {isGuaranteedCabin && (
          <Label kind="tertiary" variant="ghost" endIcon={<GuaranteedCabinTooltip />}>
            Guaranteed cabin
          </Label>
        )}
        <BodyText
          weight="bold"
          variant="medium"
          className="rate-title"
        >{rateTitle}</BodyText>
      </GridColumn>

      {(cabinRateDetailsError && !cabinRateDetailsLoading) && (
        <GridColumn>
          <BodyText variant="medium" weight="semi-bold">Oh no, an error has occurred</BodyText>
          <BodyText variant="small" >
            There seems to be a problem on our end.{' '}
            Rest assured our team is working hard to repair it and all should be fixed soon.
          </BodyText>
        </GridColumn>
      )}

      {(!cabinRateDetailsError && cabinRateDetailsLoading) && (
        <GridColumn>
          {fillArray(4).map((id) => <SkeletonInclusions key={id} />)}
        </GridColumn>
      )}

      {(!cabinRateDetailsError && !cabinRateDetailsLoading && cabinRateDetails) && (
        <GridColumn>
          <InclusionsContent>
            <HTMLRenderer
              html={handleInclusionContent}
              components={{
                ul: ({ children }) => <InclusionList>{ children }</InclusionList>,
                p: ({ children }) => <BodyText variant="small">{children}</BodyText>,
                span: ({ children }) => <BodyText variant="small">{children}</BodyText>,
                li: ({ children }) => <li><BodyText variant="small">{children}</BodyText></li>,
                button: ({ children }) => <TextButton outdent="left" kind="tertiary" size="small" onClick={onToggleTsAndCs}>{children}</TextButton>,
              }}
            />
          </InclusionsContent>
          {cabinRateDetails.hasMoreDetails && <SmallToggleButton
            size="medium"
            weight="bold"
            iconPosition="end"
            on={viewAllInclusions}
            onClick={onToggleViewAllInclusions}
          >View all inclusions</SmallToggleButton>}
        </GridColumn>
      )}

      <GridColumn className="sm-up-flex-column">
        <VerticalSpacer gap={8}>
          {!!onboardCredits && (
            <Pill
              kind="tertiary"
              variant="primary"
              endIcon={<OnboardTooltipInfo />}>
              Onboard credit <FormatCurrency
                format="dynamicDollar"
                value={onboardCredits.amount}
                currency={onboardCredits.currencyCode} />
            </Pill>
          )}
          <div>
            <Caption variant="large" colour="neutral-three">From</Caption>
            <div>
              <Heading variant="heading6" as="span">
                <FormatCurrency value={ratePrice} format="dynamicDollar"/>
              </Heading>{' '}
              <BodyText variant="small" as="span">/adult</BodyText>
            </div>
          </div>
          <SelectRateButton
            size="medium"
            kind="primary"
            onClick={onSelectRate}
            disabled={!hasDetailsContent}
          >Select</SelectRateButton>
        </VerticalSpacer>
      </GridColumn>
      <Modal title="Terms & Conditions"
      isOpen={showTsAndCs}
      onClose={onToggleTsAndCs}>
        <HTMLRenderer
          html={cabinRateDetails?.termsAndConditions}
          components={{
            ul: ({ children }) => <InclusionList>{ children }</InclusionList>,
            li: ({ children }) => <li><BodyText variant="small">{children}</BodyText></li>,
          }}
        />
      </Modal>
    </RateDetails>
  )
}

export default CabinRateDetails
